import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, 
  TrendingUp, TrendingDown, Sparkles, Heart, Star,
  CheckCircle, Clock, RefreshCw, X, Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

// Feminen renk paleti
const colors = {
  primary: "from-rose-400 to-pink-500",
  secondary: "from-violet-400 to-purple-500",
  success: "from-emerald-400 to-teal-500",
  warning: "from-amber-400 to-orange-500",
  expense: "from-rose-500 to-pink-600",
  income: "from-emerald-400 to-teal-500",
  profit: "from-violet-500 to-purple-600",
  planned: "from-amber-400 to-orange-400"
};

const SedaFinanceNew = () => {
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogType, setDialogType] = useState('expense');
  const [saving, setSaving] = useState(false); // Çift tıklama önleme
  
  // Gider kategorileri
  const EXPENSE_CATEGORIES = [
    { value: 'gida', label: 'Gıda', icon: '🍽️' },
    { value: 'yakit', label: 'Yakıt', icon: '⛽' },
    { value: 'giyim', label: 'Giyim', icon: '👕' },
    { value: 'kredi_karti', label: 'Kredi Kartı', icon: '💳' },
    { value: 'kira', label: 'Kira', icon: '🏠' },
    { value: 'aidat', label: 'Aidat', icon: '🏢' },
    { value: 'fatura', label: 'Fatura', icon: '📄' },
    { value: 'saglik', label: 'Sağlık', icon: '🏥' },
    { value: 'egitim', label: 'Eğitim', icon: '📚' },
    { value: 'ulasim', label: 'Ulaşım', icon: '🚗' },
    { value: 'diger', label: 'Diğer (Manuel)', icon: '📝' }
  ];

  // Ödeme şekilleri
  const PAYMENT_METHODS = [
    { value: 'nakit', label: 'Nakit', icon: '💵' },
    { value: 'kredi_karti', label: 'Kredi Kartı', icon: '💳' },
    { value: 'banka_karti', label: 'Banka Kartı', icon: '🏦' },
    { value: 'havale', label: 'Havale/EFT', icon: '🔄' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    date: '',
    category_name: '',
    category: '',  // Kategori kodu
    payment_method: 'nakit',  // Ödeme şekli
    is_planned: false,
    is_recurring: false,
    recurrence_months: 12, // Kaç ay düzenli olacak
    notes: ''
  });

  const fetchYearlyData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/seda/yearly-report?year=${currentYear}`,
        { withCredentials: true }
      );
      setYearlyData(res.data);
    } catch (error) {
      setYearlyData({
        months: MONTHS_TR.map((name, i) => ({
          month: i + 1,
          name,
          expenses: 0,
          income: 0,
          planned_income: 0,
          profit: 0
        })),
        totals: { expenses: 0, income: 0, planned_income: 0, profit: 0 }
      });
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  const fetchMonthlyData = useCallback(async (month) => {
    try {
      const res = await axios.get(
        `${API}/seda/monthly-report?year=${currentYear}&month=${month}`,
        { withCredentials: true }
      );
      setMonthlyData(res.data);
    } catch (error) {
      setMonthlyData({
        expenses: [],
        income: [],
        totals: { expenses: 0, income: 0, planned_income: 0, profit: 0 }
      });
    }
  }, [currentYear]);

  useEffect(() => {
    fetchYearlyData();
  }, [fetchYearlyData]);

  useEffect(() => {
    if (selectedMonth !== null) {
      fetchMonthlyData(selectedMonth);
    }
  }, [selectedMonth, fetchMonthlyData]);

  const openAddDialog = (type, isPlanned = false, isRecurring = false) => {
    setDialogType(type);
    setFormData({
      name: '',
      amount: '',
      date: selectedMonth 
        ? `${currentYear}-${String(selectedMonth).padStart(2, '0')}-01`
        : `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
      category_name: type === 'expense' ? 'Gider' : 'Gelir',
      category: '',
      payment_method: 'nakit',
      is_planned: isPlanned,
      is_recurring: isRecurring,
      recurrence_months: 12,
      notes: ''
    });
    setShowAddDialog(true);
  };

  const openEditDialog = (item, type) => {
    setDialogType(type);
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      amount: item.amount?.toString() || '',
      date: item.date || item.due_date || '',
      category_name: item.category_name || (type === 'expense' ? 'Gider' : 'Gelir'),
      category: item.category || '',
      payment_method: item.payment_method || 'nakit',
      is_planned: item.is_planned || false,
      is_recurring: item.is_recurring || false,
      recurrence_months: item.recurrence_months || 12,
      notes: item.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.amount) {
      toast.error("Ad ve tutar gerekli");
      return;
    }

    // Çift tıklama önleme
    if (saving) return;
    setSaving(true);

    try {
      // Düzenli gider instance'ları için original_id kullan
      const itemId = editingItem?.original_id || editingItem?.id;
      
      // Düzenli gider için bitiş tarihini hesapla
      let recurrenceEndDate = null;
      if (formData.is_recurring && formData.date) {
        const option = formData.recurrence_type_option || 'months';
        
        if (option === 'year_end') {
          // Yıl sonuna kadar
          const year = formData.date.substring(0, 4);
          recurrenceEndDate = `${year}-12-31`;
        } else if (option === 'end_date' && formData.recurrence_end_date) {
          // Belirli tarihe kadar
          recurrenceEndDate = formData.recurrence_end_date;
        } else {
          // Ay sayısı ile
          const startDate = new Date(formData.date);
          const endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + (formData.recurrence_months || 12) - 1);
          recurrenceEndDate = endDate.toISOString().split('T')[0];
        }
      }
      
      if (dialogType === 'expense') {
        // Kategori adını belirle
        let categoryName = formData.category_name;
        if (formData.category && formData.category !== 'diger') {
          const cat = EXPENSE_CATEGORIES.find(c => c.value === formData.category);
          if (cat) categoryName = cat.label;
        }
        
        const payload = {
          name: formData.name,
          amount: parseFloat(formData.amount),
          category_name: categoryName || 'Gider',
          category: formData.category || 'diger',
          payment_method: formData.payment_method || 'nakit',
          due_date: formData.date,
          is_recurring: formData.is_recurring,
          recurrence_type: formData.is_recurring ? 'monthly' : null,
          recurrence_end_date: recurrenceEndDate,
          is_planned: formData.is_planned,
          notes: formData.notes
        };

        if (editingItem) {
          await axios.put(`${API}/seda/expenses/${itemId}`, payload, { withCredentials: true });
          toast.success(editingItem.is_recurring_instance 
            ? "Düzenli gider güncellendi (tüm aylara yansıyacak)" 
            : "Gider güncellendi");
        } else {
          await axios.post(`${API}/seda/expenses`, payload, { withCredentials: true });
          toast.success(formData.is_recurring 
            ? "Düzenli gider eklendi"
            : "Gider eklendi");
        }
      } else {
        const payload = {
          name: formData.name,
          amount: parseFloat(formData.amount),
          category_name: formData.category_name || 'Gelir',
          date: formData.date,
          is_recurring: formData.is_recurring,
          recurrence_type: formData.is_recurring ? 'monthly' : null,
          recurrence_end_date: recurrenceEndDate,
          is_planned: formData.is_planned,
          notes: formData.notes
        };

        if (editingItem) {
          await axios.put(`${API}/seda/assets/${itemId}`, payload, { withCredentials: true });
          toast.success(editingItem.is_recurring_instance 
            ? "Düzenli gelir güncellendi (tüm aylara yansıyacak)" 
            : "Gelir güncellendi");
        } else {
          await axios.post(`${API}/seda/assets`, payload, { withCredentials: true });
          toast.success(formData.is_recurring 
            ? "Düzenli gelir eklendi"
            : "Gelir eklendi");
        }
      }

      setShowAddDialog(false);
      setShowEditDialog(false);
      setEditingItem(null);
      
      if (selectedMonth !== null) {
        fetchMonthlyData(selectedMonth);
      }
      fetchYearlyData();
    } catch (error) {
      toast.error("İşlem başarısız");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item, type, onlyThisMonth = false) => {
    // Düzenli gider instance'ları için özel uyarı
    const isRecurringInstance = item.is_recurring_instance;
    const itemId = item.original_id || item.id;
    
    let confirmMessage;
    if (isRecurringInstance) {
      if (onlyThisMonth) {
        confirmMessage = `"${item.name}" sadece bu aydan silinecek. Diğer aylar etkilenmeyecek. Emin misiniz?`;
      } else {
        confirmMessage = `"${item.name}" düzenli gider kaydı silinecek. Bu işlem TÜM AYLARA yansıyacak. Emin misiniz?`;
      }
    } else {
      confirmMessage = `"${item.name}" silinecek. Emin misiniz?`;
    }
    
    if (!window.confirm(confirmMessage)) return;

    try {
      // Düzenli gider için sadece bu ayı hariç tut
      let url;
      if (isRecurringInstance && onlyThisMonth && selectedMonth) {
        // Ay parametresi ile sadece o ayı hariç tut
        const monthStr = `${currentYear}-${String(selectedMonth).padStart(2, '0')}`;
        if (type === 'expense') {
          url = `${API}/seda/expenses/${itemId}?month=${monthStr}`;
        } else {
          url = `${API}/seda/assets/${itemId}?month=${monthStr}`;
        }
      } else {
        // Normal silme
        if (type === 'expense') {
          url = `${API}/seda/expenses/${itemId}`;
        } else {
          url = `${API}/seda/assets/${itemId}`;
        }
      }
      
      await axios.delete(url, { withCredentials: true });
      
      if (isRecurringInstance && onlyThisMonth) {
        toast.success("Bu aydan silindi");
      } else if (isRecurringInstance) {
        toast.success("Düzenli kayıt silindi (tüm aylardan kaldırıldı)");
      } else {
        toast.success("Silindi");
      }
      
      if (selectedMonth !== null) {
        fetchMonthlyData(selectedMonth);
      }
      fetchYearlyData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const handleMarkAsActual = async (item, type) => {
    const itemId = item.original_id || item.id;
    try {
      if (type === 'expense') {
        await axios.put(`${API}/seda/expenses/${itemId}`, {
          ...item,
          is_planned: false
        }, { withCredentials: true });
      } else {
        await axios.put(`${API}/seda/assets/${itemId}`, {
          ...item,
          is_planned: false
        }, { withCredentials: true });
      }
      toast.success("Gerçekleşti olarak işaretlendi");
      
      if (selectedMonth !== null) {
        fetchMonthlyData(selectedMonth);
      }
      fetchYearlyData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Yıllık Görünüm Komponenti
  const YearlyView = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Başlık */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-rose-400/20 to-pink-500/20 rounded-2xl">
            <Sparkles className="w-8 h-8 text-rose-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent">
              {currentYear} Finans Raporu
            </h1>
            <p className="text-zinc-500 mt-1">Yıllık gelir-gider özeti</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentYear(y => y - 1)}
            className="border-rose-400/30 hover:bg-rose-400/10 rounded-xl"
          >
            <ChevronLeft className="w-5 h-5 text-rose-400" />
          </Button>
          <span className="text-2xl font-bold text-rose-400 min-w-[100px] text-center">
            {currentYear}
          </span>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentYear(y => y + 1)}
            className="border-rose-400/30 hover:bg-rose-400/10 rounded-xl"
          >
            <ChevronRight className="w-5 h-5 text-rose-400" />
          </Button>
          <Button 
            onClick={fetchYearlyData}
            variant="outline"
            size="icon"
            className="border-zinc-700 rounded-xl ml-2"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Gider Kartı */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -4 }}
          className="relative overflow-hidden bg-gradient-to-br from-rose-500/10 via-pink-500/5 to-zinc-900 border border-rose-500/20 rounded-2xl p-6"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-500/20 to-transparent rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-rose-500/20 rounded-xl">
                <TrendingDown className="w-5 h-5 text-rose-400" />
              </div>
              <span className="text-sm font-medium text-rose-400/80 uppercase tracking-wider">Toplam Gider</span>
            </div>
            <p className="text-4xl font-bold text-rose-400">
              {formatCurrency(yearlyData?.totals?.expenses)}
            </p>
          </div>
        </motion.div>

        {/* Gelir Kartı */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -4 }}
          className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-zinc-900 border border-emerald-500/20 rounded-2xl p-6"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-500/20 rounded-xl">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-emerald-400/80 uppercase tracking-wider">Toplam Gelir</span>
            </div>
            <p className="text-4xl font-bold text-emerald-400">
              {formatCurrency(yearlyData?.totals?.income)}
            </p>
            {yearlyData?.totals?.planned_income > 0 && (
              <p className="text-sm text-amber-400 mt-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatCurrency(yearlyData?.totals?.planned_income)} planlanan
              </p>
            )}
          </div>
        </motion.div>

        {/* Kar Kartı */}
        <motion.div 
          whileHover={{ scale: 1.02, y: -4 }}
          className={`relative overflow-hidden bg-gradient-to-br ${
            yearlyData?.totals?.profit >= 0 
              ? 'from-violet-500/10 via-purple-500/5 border-violet-500/20' 
              : 'from-rose-500/10 via-pink-500/5 border-rose-500/20'
          } to-zinc-900 border rounded-2xl p-6`}
        >
          <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${
            yearlyData?.totals?.profit >= 0 ? 'from-violet-500/20' : 'from-rose-500/20'
          } to-transparent rounded-full blur-3xl`} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${yearlyData?.totals?.profit >= 0 ? 'bg-violet-500/20' : 'bg-rose-500/20'}`}>
                <Star className={`w-5 h-5 ${yearlyData?.totals?.profit >= 0 ? 'text-violet-400' : 'text-rose-400'}`} />
              </div>
              <span className={`text-sm font-medium uppercase tracking-wider ${
                yearlyData?.totals?.profit >= 0 ? 'text-violet-400/80' : 'text-rose-400/80'
              }`}>Net Kar</span>
            </div>
            <p className={`text-4xl font-bold ${yearlyData?.totals?.profit >= 0 ? 'text-violet-400' : 'text-rose-400'}`}>
              {formatCurrency(yearlyData?.totals?.profit)}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Yıllık Tablo - 3 Sütun */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* GİDER TABLOSU */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white text-center py-4 font-bold text-lg">
            <TrendingDown className="w-5 h-5 inline mr-2" />
            Aylık Giderler
          </div>
          <div className="divide-y divide-zinc-800/50">
            {yearlyData?.months?.map((month, i) => (
              <div 
                key={i}
                onClick={() => setSelectedMonth(i + 1)}
                className="flex items-center justify-between px-5 py-3 cursor-pointer transition-all hover:bg-rose-500/10"
                data-testid={`month-row-expense-${i+1}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm w-6">{i + 1}</span>
                  <span className="text-zinc-300">{month.name}</span>
                </div>
                <span className={`font-mono ${month.expenses > 0 ? 'text-rose-400' : 'text-zinc-600'}`}>
                  {formatCurrency(month.expenses)}
                </span>
              </div>
            ))}
          </div>
          <div className="bg-rose-500/10 px-5 py-4 flex items-center justify-between">
            <span className="text-rose-400 font-bold">TOPLAM</span>
            <span className="font-mono font-bold text-rose-400 text-lg">
              {formatCurrency(yearlyData?.totals?.expenses)}
            </span>
          </div>
        </div>

        {/* GELİR TABLOSU */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center py-4 font-bold text-lg">
            <TrendingUp className="w-5 h-5 inline mr-2" />
            Aylık Gelirler
          </div>
          <div className="divide-y divide-zinc-800/50">
            {yearlyData?.months?.map((month, i) => (
              <div 
                key={i}
                onClick={() => setSelectedMonth(i + 1)}
                className="flex items-center justify-between px-5 py-3 cursor-pointer transition-all hover:bg-emerald-500/10"
                data-testid={`month-row-income-${i+1}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm w-6">{i + 1}</span>
                  <span className="text-zinc-300">{month.name}</span>
                </div>
                <div className="text-right">
                  <span className={`font-mono ${month.income > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>
                    {formatCurrency(month.income)}
                  </span>
                  {month.planned_income > 0 && (
                    <span className="text-xs text-amber-400 block">
                      +{formatCurrency(month.planned_income)} planlanan
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-emerald-500/10 px-5 py-4 flex items-center justify-between">
            <span className="text-emerald-400 font-bold">TOPLAM</span>
            <span className="font-mono font-bold text-emerald-400 text-lg">
              {formatCurrency(yearlyData?.totals?.income)}
            </span>
          </div>
        </div>

        {/* KAR TABLOSU */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-center py-4 font-bold text-lg">
            <Star className="w-5 h-5 inline mr-2" />
            Aylık Kar
          </div>
          <div className="divide-y divide-zinc-800/50">
            {yearlyData?.months?.map((month, i) => (
              <div 
                key={i}
                onClick={() => setSelectedMonth(i + 1)}
                className="flex items-center justify-between px-5 py-3 cursor-pointer transition-all hover:bg-violet-500/10"
                data-testid={`month-row-profit-${i+1}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm w-6">{i + 1}</span>
                  <span className="text-zinc-300">{month.name}</span>
                </div>
                <span className={`font-mono font-semibold ${month.profit >= 0 ? 'text-violet-400' : 'text-rose-400'}`}>
                  {formatCurrency(month.profit)}
                </span>
              </div>
            ))}
          </div>
          <div className={`px-5 py-4 flex items-center justify-between ${
            yearlyData?.totals?.profit >= 0 ? 'bg-violet-500/10' : 'bg-rose-500/10'
          }`}>
            <span className={`font-bold ${yearlyData?.totals?.profit >= 0 ? 'text-violet-400' : 'text-rose-400'}`}>
              NET KAR
            </span>
            <span className={`font-mono font-bold text-lg ${
              yearlyData?.totals?.profit >= 0 ? 'text-violet-400' : 'text-rose-400'
            }`}>
              {formatCurrency(yearlyData?.totals?.profit)}
            </span>
          </div>
        </div>
      </div>

      {/* Bilgi Notu */}
      <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
        <Heart className="w-4 h-4 text-rose-400" />
        <span>Detay görmek için aya tıklayın</span>
      </div>
    </motion.div>
  );

  // Aylık Görünüm Komponenti
  const MonthlyView = () => (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Başlık ve Navigasyon */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost"
            onClick={() => setSelectedMonth(null)}
            className="text-rose-400 hover:text-rose-300 hover:bg-rose-400/10 rounded-xl"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Yıllık Rapora Dön
          </Button>
        </div>
        
        <h1 className="text-2xl font-bold text-zinc-50">
          <span className="bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent">
            {MONTHS_TR[selectedMonth - 1]}
          </span>
          {' '}{currentYear}
        </h1>

        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedMonth(m => m > 1 ? m - 1 : 12)}
            className="border-rose-400/30 hover:bg-rose-400/10 rounded-xl"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedMonth(m => m < 12 ? m + 1 : 1)}
            className="border-rose-400/30 hover:bg-rose-400/10 rounded-xl"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mini Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
          <p className="text-xs text-rose-400/80 uppercase tracking-wider mb-1">Gider</p>
          <p className="text-xl font-bold text-rose-400">{formatCurrency(monthlyData?.totals?.expenses)}</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
          <p className="text-xs text-emerald-400/80 uppercase tracking-wider mb-1">Gerçekleşen Gelir</p>
          <p className="text-xl font-bold text-emerald-400">{formatCurrency(monthlyData?.totals?.actual_income)}</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-xs text-amber-400/80 uppercase tracking-wider mb-1">Planlanan Gelir</p>
          <p className="text-xl font-bold text-amber-400">{formatCurrency(monthlyData?.totals?.planned_income)}</p>
        </div>
        <div className={`${monthlyData?.totals?.profit >= 0 ? 'bg-violet-500/10 border-violet-500/20' : 'bg-rose-500/10 border-rose-500/20'} border rounded-xl p-4`}>
          <p className={`text-xs uppercase tracking-wider mb-1 ${monthlyData?.totals?.profit >= 0 ? 'text-violet-400/80' : 'text-rose-400/80'}`}>Net Kar</p>
          <p className={`text-xl font-bold ${monthlyData?.totals?.profit >= 0 ? 'text-violet-400' : 'text-rose-400'}`}>
            {formatCurrency(monthlyData?.totals?.profit)}
          </p>
        </div>
      </div>

      {/* 2 Sütunlu Detay Tabloları */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GİDER TABLOSU */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white py-4 px-5 font-bold text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              GİDERLER
            </div>
            <Button 
              onClick={() => openAddDialog('expense')}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1" /> Ekle
            </Button>
          </div>
          
          {/* Planlanan vs Gerçekleşen vs Düzenli Legend */}
          <div className="px-5 py-2 bg-zinc-800/30 border-b border-zinc-800 flex gap-4 text-xs flex-wrap">
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-rose-400 rounded-full" /> Gerçekleşen
            </span>
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-amber-400 rounded-full" /> Planlanan (P)
            </span>
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-violet-400 rounded-full" /> Düzenli (D)
            </span>
          </div>
          
          <div className="divide-y divide-zinc-800/50 max-h-[400px] overflow-y-auto">
            {monthlyData?.expenses?.length > 0 ? (
              monthlyData.expenses.map((item, i) => (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => openEditDialog(item, 'expense')}
                  className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-rose-500/5 transition-colors group ${
                    item.is_planned ? 'bg-amber-500/5' : ''
                  }`}
                  data-testid={`expense-row-${item.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-zinc-500 text-sm w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 truncate flex items-center gap-2">
                        {item.name}
                        {item.is_planned && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-400/20 text-amber-400 rounded">P</span>
                        )}
                        {(item.is_recurring || item.is_recurring_instance) && (
                          <span className="text-xs px-1.5 py-0.5 bg-violet-400/20 text-violet-400 rounded">D</span>
                        )}
                      </p>
                      <p className="text-xs text-zinc-500 flex items-center gap-2">
                        <span>{formatDate(item.due_date)}</span>
                        {item.category && item.category !== 'diger' && (
                          <span className="px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-400">
                            {EXPENSE_CATEGORIES.find(c => c.value === item.category)?.icon || '📝'} {item.category_name || item.category}
                          </span>
                        )}
                        {item.payment_method && (
                          <span className="text-zinc-500">
                            {item.payment_method === 'nakit' ? '💵' : item.payment_method === 'kredi_karti' ? '💳' : item.payment_method === 'banka_karti' ? '🏦' : '🔄'}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono ${item.is_planned ? 'text-amber-400' : 'text-rose-400'}`}>
                      {formatCurrency(item.amount)}
                    </span>
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                      {item.is_planned && !item.is_recurring_instance && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsActual(item, 'expense'); }}
                          className="h-7 w-7 text-emerald-400 hover:bg-emerald-400/10 rounded-lg"
                          title="Gerçekleşti olarak işaretle"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Tüm giderler için silme butonu - düzenli olanlar için sadece bu ay silinir */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDelete(item, 'expense', item.is_recurring_instance); 
                        }}
                        className="h-7 w-7 text-rose-400 hover:bg-rose-400/10 rounded-lg"
                        title={item.is_recurring_instance ? "Bu aydan sil" : "Sil"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="py-12 text-center text-zinc-500">
                <TrendingDown className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>Henüz gider kaydı yok</p>
              </div>
            )}
          </div>
          
          <div className="bg-rose-500/10 px-5 py-4 flex items-center justify-between border-t border-zinc-800">
            <span className="text-rose-400 font-bold">TOPLAM GİDER</span>
            <span className="font-mono font-bold text-rose-400 text-lg">
              {formatCurrency(monthlyData?.totals?.expenses)}
            </span>
          </div>
          
          {/* Planlanan ve Düzenli Ekle Butonları */}
          <div className="px-5 py-3 border-t border-zinc-800 grid grid-cols-2 gap-2">
            <Button 
              onClick={() => openAddDialog('expense', true)}
              variant="outline"
              className="border-amber-400/30 text-amber-400 hover:bg-amber-400/10 rounded-xl text-sm"
            >
              <Clock className="w-4 h-4 mr-1" /> Planlanan
            </Button>
            <Button 
              onClick={() => {
                setDialogType('expense');
                setFormData({
                  name: '',
                  amount: '',
                  date: selectedMonth 
                    ? `${currentYear}-${String(selectedMonth).padStart(2, '0')}-01`
                    : `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
                  category_name: 'Gider',
                  is_planned: false,
                  is_recurring: true,
                  recurrence_months: 12,
                  notes: ''
                });
                setShowAddDialog(true);
              }}
              variant="outline"
              className="border-violet-400/30 text-violet-400 hover:bg-violet-400/10 rounded-xl text-sm"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Düzenli
            </Button>
          </div>
        </div>

        {/* GELİR TABLOSU */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 px-5 font-bold text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              GELİRLER
            </div>
            <Button 
              onClick={() => openAddDialog('income')}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1" /> Ekle
            </Button>
          </div>
          
          {/* Tahsil Edildi vs Beklenen vs Düzenli Legend */}
          <div className="px-5 py-2 bg-zinc-800/30 border-b border-zinc-800 flex gap-4 text-xs flex-wrap">
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-emerald-400 rounded-full" /> Tahsil Edildi
            </span>
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-amber-400 rounded-full" /> Beklenen (P)
            </span>
            <span className="flex items-center gap-1 text-zinc-400">
              <div className="w-2 h-2 bg-violet-400 rounded-full" /> Düzenli (D)
            </span>
          </div>
          
          <div className="divide-y divide-zinc-800/50 max-h-[400px] overflow-y-auto">
            {monthlyData?.income?.length > 0 ? (
              monthlyData.income.map((item, i) => (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => openEditDialog(item, 'income')}
                  className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-emerald-500/5 transition-colors group ${
                    item.is_planned ? 'bg-amber-500/5' : ''
                  }`}
                  data-testid={`income-row-${item.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-zinc-500 text-sm w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 truncate flex items-center gap-2">
                        {item.name}
                        {item.is_planned && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-400/20 text-amber-400 rounded">P</span>
                        )}
                        {(item.is_recurring || item.is_recurring_instance) && (
                          <span className="text-xs px-1.5 py-0.5 bg-violet-400/20 text-violet-400 rounded">D</span>
                        )}
                      </p>
                      <p className="text-xs text-zinc-500">{formatDate(item.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono ${item.is_planned ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {formatCurrency(item.amount)}
                    </span>
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                      {item.is_planned && !item.is_recurring_instance && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsActual(item, 'income'); }}
                          className="h-7 w-7 text-emerald-400 hover:bg-emerald-400/10 rounded-lg"
                          title="Tahsil edildi olarak işaretle"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Tüm gelirler için silme butonu - düzenli olanlar için sadece bu ay silinir */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDelete(item, 'income', item.is_recurring_instance); 
                        }}
                        className="h-7 w-7 text-rose-400 hover:bg-rose-400/10 rounded-lg"
                        title={item.is_recurring_instance ? "Bu aydan sil" : "Sil"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="py-12 text-center text-zinc-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>Henüz gelir kaydı yok</p>
              </div>
            )}
          </div>
          
          <div className="bg-emerald-500/10 px-5 py-4 flex items-center justify-between border-t border-zinc-800">
            <span className="text-emerald-400 font-bold">TOPLAM GELİR</span>
            <span className="font-mono font-bold text-emerald-400 text-lg">
              {formatCurrency(monthlyData?.totals?.income)}
            </span>
          </div>
          
          {/* Planlanan ve Düzenli Ekle Butonları */}
          <div className="px-5 py-3 border-t border-zinc-800 grid grid-cols-2 gap-2">
            <Button 
              onClick={() => openAddDialog('income', true)}
              variant="outline"
              className="border-amber-400/30 text-amber-400 hover:bg-amber-400/10 rounded-xl text-sm"
            >
              <Clock className="w-4 h-4 mr-1" /> Beklenen
            </Button>
            <Button 
              onClick={() => {
                setDialogType('income');
                setFormData({
                  name: '',
                  amount: '',
                  date: selectedMonth 
                    ? `${currentYear}-${String(selectedMonth).padStart(2, '0')}-01`
                    : `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
                  category_name: 'Gelir',
                  is_planned: false,
                  is_recurring: true,
                  recurrence_months: 12,
                  notes: ''
                });
                setShowAddDialog(true);
              }}
              variant="outline"
              className="border-violet-400/30 text-violet-400 hover:bg-violet-400/10 rounded-xl text-sm"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Düzenli
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading && !yearlyData) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-zinc-400 mt-4">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const isDialogOpen = showAddDialog || showEditDialog;
  const closeDialog = () => { 
    setShowAddDialog(false); 
    setShowEditDialog(false); 
    setEditingItem(null); 
  };

  return (
    <div className="p-4 lg:p-6" data-testid="seda-finance-new-page">
      <AnimatePresence mode="wait">
        {selectedMonth === null ? <YearlyView key="yearly" /> : <MonthlyView key="monthly" />}
      </AnimatePresence>

      {/* Form Dialog - Inline to prevent re-mounting on each keystroke */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              {dialogType === 'expense' ? (
                <><TrendingDown className="w-5 h-5 text-rose-400" /> {editingItem ? 'Gider Düzenle' : 'Yeni Gider'}</>
              ) : (
                <><TrendingUp className="w-5 h-5 text-emerald-400" /> {editingItem ? 'Gelir Düzenle' : 'Yeni Gelir'}</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Açıklama *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-zinc-950 border-zinc-700 rounded-xl"
                placeholder="Ör: Ofis Kirası"
                data-testid="form-name-input"
              />
            </div>
            
            <div>
              <Label className="text-zinc-400">Tutar (TL) *</Label>
              <Input 
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                className="mt-1 bg-zinc-950 border-zinc-700 rounded-xl"
                placeholder="0"
                data-testid="form-amount-input"
              />
            </div>
            
            <div>
              <Label className="text-zinc-400">Tarih</Label>
              <Input 
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1 bg-zinc-950 border-zinc-700 rounded-xl"
                data-testid="form-date-input"
              />
            </div>

            {/* Gider Kategorisi - Sadece gider için */}
            {dialogType === 'expense' && (
              <div>
                <Label className="text-zinc-400">Gider Kategorisi</Label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const cat = EXPENSE_CATEGORIES.find(c => c.value === e.target.value);
                    setFormData(prev => ({ 
                      ...prev, 
                      category: e.target.value,
                      category_name: cat ? cat.label : prev.category_name
                    }));
                  }}
                  className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 text-white"
                  data-testid="form-category-select"
                >
                  <option value="">-- Kategori Seçin --</option>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Manuel Kategori Girişi - "Diğer" seçildiğinde */}
            {dialogType === 'expense' && formData.category === 'diger' && (
              <div>
                <Label className="text-zinc-400">Kategori Adı (Manuel)</Label>
                <Input 
                  value={formData.category_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, category_name: e.target.value }))}
                  className="mt-1 bg-zinc-950 border-zinc-700 rounded-xl"
                  placeholder="Kategori adını yazın"
                  data-testid="form-category-manual"
                />
              </div>
            )}

            {/* Ödeme Şekli - Sadece gider için */}
            {dialogType === 'expense' && (
              <div>
                <Label className="text-zinc-400">Ödeme Şekli</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(method => (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, payment_method: method.value }))}
                      className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                        formData.payment_method === method.value
                          ? 'bg-amber-500 text-black' 
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span>{method.icon}</span>
                      <span>{method.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-amber-400/10 border border-amber-400/30 rounded-xl">
              <Switch
                id="is_planned"
                checked={formData.is_planned}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_planned: checked }))}
              />
              <label htmlFor="is_planned" className="text-amber-400 text-sm cursor-pointer">
                <Clock className="w-4 h-4 inline mr-1" />
                {dialogType === 'expense' ? 'Planlanan Gider' : 'Beklenen Gelir'} (Henüz gerçekleşmedi)
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_recurring"
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
              />
              <label htmlFor="is_recurring" className="text-zinc-400 text-sm cursor-pointer">
                Düzenli (Her ay tekrarlanan)
              </label>
            </div>

            {/* Düzenli gider için süre seçimi */}
            {formData.is_recurring && (
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 space-y-4">
                <Label className="text-violet-400 text-sm font-medium">Tekrar Süresi</Label>
                
                {/* Süre tipi seçimi */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recurrence_type_option: 'year_end' }))}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      (formData.recurrence_type_option || 'months') === 'year_end'
                        ? 'bg-violet-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Yıl Sonuna
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recurrence_type_option: 'months' }))}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      (formData.recurrence_type_option || 'months') === 'months'
                        ? 'bg-violet-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Ay Sayısı
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recurrence_type_option: 'end_date' }))}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      formData.recurrence_type_option === 'end_date'
                        ? 'bg-violet-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Tarihe Kadar
                  </button>
                </div>

                {/* Ay sayısı seçimi */}
                {(formData.recurrence_type_option || 'months') === 'months' && (
                  <div>
                    <div className="grid grid-cols-6 gap-2">
                      {[1, 2, 3, 6, 9, 12].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, recurrence_months: m }))}
                          className={`py-2 rounded-lg text-sm font-medium transition-all ${
                            (formData.recurrence_months || 12) === m 
                              ? 'bg-emerald-500 text-white' 
                              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-zinc-500 text-sm">veya</span>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        placeholder="Özel ay sayısı"
                        value={formData.custom_months || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setFormData(prev => ({ 
                            ...prev, 
                            custom_months: e.target.value,
                            recurrence_months: val > 0 ? val : prev.recurrence_months 
                          }));
                        }}
                        className="w-32 bg-zinc-800/50 border-zinc-700 text-zinc-200"
                      />
                      <span className="text-zinc-500 text-sm">ay</span>
                    </div>
                  </div>
                )}

                {/* Bitiş tarihi seçimi */}
                {formData.recurrence_type_option === 'end_date' && (
                  <div>
                    <Label className="text-zinc-400 text-sm">Bitiş Tarihi</Label>
                    <Input
                      type="date"
                      value={formData.recurrence_end_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                      className="mt-1 bg-zinc-800/50 border-zinc-700 text-zinc-200"
                    />
                  </div>
                )}

                {/* Özet bilgi */}
                <p className="text-xs text-zinc-500 border-t border-zinc-700 pt-3">
                  {formData.date && (
                    <>
                      {(formData.recurrence_type_option || 'months') === 'year_end' && (
                        `${formData.date} tarihinden ${formData.date?.substring(0,4)} yıl sonuna kadar her ay tekrarlanacak`
                      )}
                      {(formData.recurrence_type_option || 'months') === 'months' && (
                        `${formData.date} tarihinden itibaren ${formData.recurrence_months || 12} ay boyunca tekrarlanacak`
                      )}
                      {formData.recurrence_type_option === 'end_date' && formData.recurrence_end_date && (
                        `${formData.date} - ${formData.recurrence_end_date} tarihleri arasında her ay tekrarlanacak`
                      )}
                    </>
                  )}
                </p>
              </div>
            )}

            <div>
              <Label className="text-zinc-400">Notlar</Label>
              <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1 bg-zinc-950 border-zinc-700 rounded-xl"
                placeholder="Ek notlar..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {editingItem && !editingItem.is_recurring_instance && (
                <Button 
                  variant="destructive"
                  onClick={() => {
                    handleDelete(editingItem, dialogType);
                    closeDialog();
                  }}
                  className="bg-rose-500 hover:bg-rose-400 rounded-xl"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Sil
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog} className="border-zinc-700 rounded-xl" disabled={saving}>
                İptal
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className={`rounded-xl ${dialogType === 'expense' ? 'bg-rose-500 hover:bg-rose-400' : 'bg-emerald-500 hover:bg-emerald-400'} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="form-save-btn"
              >
                {saving ? 'Kaydediliyor...' : (editingItem ? 'Güncelle' : 'Ekle')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SedaFinanceNew;
